/**
 * FactoryJourneyContextContract (AJIP-16.02.009 · §10 · F6)
 * ---------------------------------------------------------------------
 * Framework-agnostic validation of factory-specific context.
 *
 * F6 canonical fields (context contract):
 *   • principalRef      — acting principal (audit only, not identity)
 *   • organisationRef   — controlling organisation
 *   • factorySubjectRef — canonical factory subject
 *   • correlationRef    — request tracing (not identity)
 *
 * Additional serialisable metadata may accompany the context provided
 * no forbidden key, no executable value, and no byte-cap violation.
 */
import { InvalidFactoryContextError } from './FactoryJourneyErrors';

const MAX_FACTORY_CONTEXT_BYTES = 8 * 1024;

const REQUIRED_FACTORY_CONTEXT_FIELDS = Object.freeze([
  'principalRef',
  'organisationRef',
  'factorySubjectRef',
]);
// correlationRef is required-shape when supplied but is not deterministic-identity input.
const OPTIONAL_FACTORY_CONTEXT_FIELDS = Object.freeze(['correlationRef']);

const FORBIDDEN_KEYS = Object.freeze([
  'password', 'token', 'apikey', 'secret', 'credential',
  'cardnumber', 'cvv', 'privatekey', 'sessionid',
  'accesstoken', 'refreshtoken', 'bearer', 'authorization',
  'providersession', 'paymentinfo',
]);

const isPlainObject = (v) => (
  v !== null && typeof v === 'object' && !Array.isArray(v)
  && (Object.getPrototypeOf(v) === Object.prototype || Object.getPrototypeOf(v) === null)
);
const isNonEmptyString = (v) => typeof v === 'string' && v.trim().length > 0;

const walkForbidden = (value, path = 'context') => {
  if (value === null || value === undefined) return;
  if (typeof value === 'function') {
    throw new InvalidFactoryContextError(`Executable data at ${path} prohibited`);
  }
  if (Array.isArray(value)) {
    value.forEach((item, i) => walkForbidden(item, `${path}[${i}]`));
    return;
  }
  if (typeof value !== 'object') return;
  if (!isPlainObject(value)) {
    throw new InvalidFactoryContextError(`${path} must be plain object`);
  }
  for (const [k, v] of Object.entries(value)) {
    if (FORBIDDEN_KEYS.includes(k.toLowerCase())) {
      throw new InvalidFactoryContextError(`Forbidden key '${k}' at ${path}`);
    }
    walkForbidden(v, `${path}.${k}`);
  }
};

const validateFactoryContext = (ctx, { strict = false } = {}) => {
  if (ctx === null || ctx === undefined) {
    if (strict) throw new InvalidFactoryContextError('Context required in strict mode');
    return true;
  }
  if (!isPlainObject(ctx)) {
    throw new InvalidFactoryContextError('Context must be plain object');
  }
  walkForbidden(ctx);
  if (strict) {
    for (const f of REQUIRED_FACTORY_CONTEXT_FIELDS) {
      if (!isNonEmptyString(ctx[f])) {
        throw new InvalidFactoryContextError(`Missing required context field: ${f}`);
      }
    }
    if ('correlationRef' in ctx && ctx.correlationRef !== null
        && !isNonEmptyString(ctx.correlationRef)) {
      throw new InvalidFactoryContextError('correlationRef must be non-empty string or null');
    }
  }
  let s;
  try { s = JSON.stringify(ctx); } catch (_e) {
    throw new InvalidFactoryContextError('Not serialisable');
  }
  const bytes = typeof Buffer !== 'undefined' ? Buffer.byteLength(s, 'utf8') : s.length;
  if (bytes > MAX_FACTORY_CONTEXT_BYTES) {
    throw new InvalidFactoryContextError('Context exceeds byte cap');
  }
  return true;
};

export {
  validateFactoryContext,
  MAX_FACTORY_CONTEXT_BYTES,
  REQUIRED_FACTORY_CONTEXT_FIELDS,
  OPTIONAL_FACTORY_CONTEXT_FIELDS,
  FORBIDDEN_KEYS,
};
